import { FluentProvider, type Theme } from "@fluentui/react-components";
import type React from "react";
import { perfMark } from "../perf";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  KeycloakAuthProvider,
  type KeycloakAuthProviderProps,
} from "@adapt/auth";
import {
  loadRuntimeConfig,
  type AdaptRuntimeConfig,
  type LoadRuntimeConfigOptions,
} from "@adapt/auth";
import {
  AdaptThemeContext,
  persistAdaptThemeMode,
  resolveAdaptTheme,
  resolveInitialAdaptThemeMode,
  type AdaptThemeMode,
} from "../theme/adaptTheme";

export interface AdaptShellProviderProps {
  children: ReactNode;
  authenticate?: boolean;
  config?: AdaptRuntimeConfig;
  configOptions?: LoadRuntimeConfigOptions;
  defaultThemeMode?: AdaptThemeMode;
  dir?: "ltr" | "rtl";
  errorFallback?: (error: Error) => ReactNode;
  keycloakOptions?: Omit<KeycloakAuthProviderProps, "children" | "config">;
  loadingFallback?: ReactNode;
  persistThemeMode?: boolean;
  theme?: Theme;
  themeMode?: AdaptThemeMode;
  themeStorageKey?: string;
}

const defaultLoadingFallback = (
  <div style={{ padding: "1rem" }}>Loading ADAPT shell...</div>
);

export const AdaptShellProvider = ({
  children,
  authenticate = true,
  config: providedConfig,
  configOptions,
  defaultThemeMode = "light",
  dir = "ltr",
  errorFallback,
  keycloakOptions,
  loadingFallback = defaultLoadingFallback,
  persistThemeMode = true,
  theme,
  themeMode,
  themeStorageKey,
}: AdaptShellProviderProps): React.JSX.Element => {
  const [config, setConfig] = useState<AdaptRuntimeConfig | null>(
    providedConfig ?? null,
  );
  const [error, setError] = useState<Error | null>(null);
  const [uncontrolledThemeMode, setUncontrolledThemeMode] =
    useState<AdaptThemeMode>(
      () =>
        themeMode ??
        resolveInitialAdaptThemeMode({
          defaultMode: defaultThemeMode,
          storageKey: themeStorageKey,
        }),
    );
  const resolvedThemeMode = themeMode ?? uncontrolledThemeMode;

  const setThemeMode = useCallback(
    (nextThemeMode: AdaptThemeMode) => {
      setUncontrolledThemeMode(nextThemeMode);
      if (persistThemeMode) {
        persistAdaptThemeMode(nextThemeMode, themeStorageKey);
      }
    },
    [persistThemeMode, themeStorageKey],
  );

  const toggleThemeMode = useCallback(() => {
    setThemeMode(resolvedThemeMode === "dark" ? "light" : "dark");
  }, [resolvedThemeMode, setThemeMode]);

  const themeContextValue = useMemo(
    () => ({
      themeMode: resolvedThemeMode,
      setThemeMode,
      toggleThemeMode,
    }),
    [resolvedThemeMode, setThemeMode, toggleThemeMode],
  );

  useEffect(() => {
    if (providedConfig) {
      setConfig(providedConfig);
      return;
    }

    perfMark("SHELL:config-fetch-start");
    let mounted = true;
    loadRuntimeConfig(configOptions)
      .then((loadedConfig) => {
        if (mounted) {
          perfMark("SHELL:config-loaded", { env: loadedConfig.environment });
          setConfig(loadedConfig);
        }
      })
      .catch((configError: unknown) => {
        const normalisedError =
          configError instanceof Error
            ? configError
            : new Error("Unable to load ADAPT config.");
        if (mounted) setError(normalisedError);
      });

    return () => {
      mounted = false;
    };
  }, [configOptions, providedConfig]);

  if (error) {
    return (
      <>
        {errorFallback ? (
          errorFallback(error)
        ) : (
          <div role="alert">{error.message}</div>
        )}
      </>
    );
  }

  if (!config) return <>{loadingFallback}</>;

  const content = authenticate ? (
    <KeycloakAuthProvider config={config} {...keycloakOptions}>
      {children}
    </KeycloakAuthProvider>
  ) : (
    children
  );

  return (
    <AdaptThemeContext.Provider value={themeContextValue}>
      <FluentProvider
        dir={dir}
        theme={theme ?? resolveAdaptTheme(resolvedThemeMode)}
        style={{
          // Expose the header height as a CSS custom property so all
          // descendants (header, body padding, nav) can share the value
          // — matches --app-header-height in the legacy ClassNames.ts.
          "--app-header-height": "48px",
          "--app-header-btn-width": "48px",
        } as React.CSSProperties}
      >
        {content}
      </FluentProvider>
    </AdaptThemeContext.Provider>
  );
};
